import Vue from 'vue';
import App from './App.vue';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import Router from 'vue-router';
import LoadProfileDetails from './components/LoadProfileDetails.vue';
import AuthPage from './pages/AuthPage.vue';
import ProcessRunner from './components/ProcessRunner.vue';
import redirect from './components/redirect.vue';
import Impressum from './components/Impressum.vue';
import Privacy from './components/Privacy.vue';
import Home from './components/Home.vue';
import AccessDenied from './components/AccessDenied.vue';
import PageNotFound from './components/PageNotFound.vue';

//amplify
import Amplify, * as AmplifyModules from 'aws-amplify';
import { AmplifyPlugin } from 'aws-amplify-vue';
import awsconfig from './aws-exports-custom';
import { AmplifyEventBus } from 'aws-amplify-vue';
import store from './store/store';
import CryptoJS from 'crypto-js';
import {
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

// Define userpool based on URL
const cognitoClient = awsconfig.codelos;
const crypto_secret = process.env.VUE_APP_CRYPTO_SECRET;
// if (location.href.includes('.team-energie.')) {
//   cognitoClient = awsconfig.teamenergie;
// }

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: cognitoClient.aws_user_pools_id,
  ClientId: cognitoClient.aws_user_pools_web_client_id,
});

Amplify.configure(cognitoClient);
Vue.use(AmplifyPlugin, AmplifyModules);

import { BootstrapVue, IconsPlugin } from 'bootstrap-vue';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';

Vue.use(BootstrapVue);
Vue.use(IconsPlugin);

//vue-recource for restful apis
import VueResource from 'vue-resource';
Vue.use(VueResource);

import AsyncComputed from 'vue-async-computed';
Vue.use(AsyncComputed);

import vueKanban from 'vue-kanban';
import { isUserNotLoggedIn } from './router/guards/auth.guard';
Vue.use(vueKanban);

Vue.use(Router);
Vue.config.productionTip = false;

import { pageBuilderPlugins } from '@codelos.io/page-builder';
Vue.use(pageBuilderPlugins);

import TooltipDirective from './utils/directive/tooltipDirective';
Vue.directive('tooltip', TooltipDirective);

let user;
let mounted = false;
let authSyncTimeout = null;
let authSyncInProgress = false;
let authSyncPending = false;
let lastAuthSyncAt = 0;
let lastSyncedToken = null;

const cognitoStorageKeyPrefix = `CognitoIdentityServiceProvider.${cognitoClient.aws_user_pools_web_client_id}.`;
const authSyncThrottleMs = 1200;
const isRoutePublic = (route) =>
  route?.matched?.some((record) => record?.meta?.requiresAuth === false) ||
  false;
const isRouteProtected = (route) =>
  route?.matched?.some((record) => record?.meta?.requiresAuth === true) ||
  false;

const setAdminStatus = (data) => {
  const email = data?.attributes?.email || '';
  const adminMails = [
    'codelos.io',
    'esparkinfo.com',
    'esparkbiztech.com',
    'esparkbiztechnologies.com',
  ];
  const isAdmin =
    email.includes('@') && adminMails.includes(email.split('@')[1]);
  store.commit('setIsAdmin', isAdmin);
};

const handleCrossTabSignedOut = () => {
  mounted = false;
  store.commit('clearStore');
  const currentRoute = router?.currentRoute;
  if (currentRoute?.name !== 'auth' && isRouteProtected(currentRoute)) {
    router.push({
      path: '/auth',
      query: {
        redirect: currentRoute?.fullPath || '/',
      },
    });
  }
};

const handleCrossTabSignedIn = (authUser) => {
  if (!authUser || !authUser.signInUserSession) return;
  store.commit('setUser', authUser);
  setAdminStatus(authUser);

  if (router?.currentRoute?.name === 'auth') {
    let path = decodeURIComponent(
      router.history.current.fullPath
        .replace('/auth?redirect=', '')
        .replace('/auth', '/')
    );
    router.push({ path: path });
  }
};

const getStorageSessionToken = () => {
  const clientId = cognitoClient?.aws_user_pools_web_client_id;
  if (!clientId) return null;
  const lastAuthUser = localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
  );
  if (!lastAuthUser) return null;
  return localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.accessToken`
  );
};

const getStoreSessionToken = () => store.getters.getJWTToken || null;

const canSkipAuthSync = () => {
  const storageToken = getStorageSessionToken();
  const storeToken = getStoreSessionToken();
  if (storageToken === storeToken) {
    if (storageToken) return true;
    const currentRoute = router?.currentRoute;
    return currentRoute?.name === 'auth' || !isRouteProtected(currentRoute);
  }
  return false;
};

const syncAuthStateAcrossTabs = async () => {
  if (authSyncInProgress) {
    authSyncPending = true;
    return;
  }
  if (canSkipAuthSync()) return;

  authSyncInProgress = true;
  try {
    const authUser = await AmplifyModules.Auth.currentAuthenticatedUser();
    if (authUser?.signInUserSession) {
      lastSyncedToken =
        authUser.signInUserSession?.accessToken?.jwtToken || null;
      handleCrossTabSignedIn(authUser);
    } else {
      lastSyncedToken = null;
      handleCrossTabSignedOut();
    }
  } catch (error) {
    lastSyncedToken = null;
    handleCrossTabSignedOut();
  } finally {
    authSyncInProgress = false;
    lastAuthSyncAt = Date.now();
    if (authSyncPending) {
      authSyncPending = false;
      queueAuthStateSync(true);
    }
  }
};

const queueAuthStateSync = (force = false) => {
  if (!force) {
    const now = Date.now();
    const isThrottled = now - lastAuthSyncAt < authSyncThrottleMs;
    const storageToken = getStorageSessionToken();
    const storeToken = getStoreSessionToken();
    const noAuthChange =
      storageToken === storeToken && storageToken === lastSyncedToken;

    if (isThrottled || noAuthChange) return;
  }

  if (authSyncTimeout) {
    clearTimeout(authSyncTimeout);
  }
  authSyncTimeout = setTimeout(syncAuthStateAcrossTabs, 120);
};

const setupAuthStateSyncListeners = () => {
  window.addEventListener('storage', (event) => {
    if (event.storageArea !== localStorage) return;

    // key === null means localStorage.clear() was called in another tab
    if (
      event.key === null ||
      (event.key && event.key.startsWith(cognitoStorageKeyPrefix))
    ) {
      queueAuthStateSync();
    }
  });

  // Catch missed storage events and keep token/session fresh when tab becomes active.
  window.addEventListener('focus', queueAuthStateSync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      queueAuthStateSync();
    }
  });
};

AmplifyEventBus.$on('authState', async (state) => {
  if (state === 'signedOut') {
    // Reset all values
    mounted = false;
    store.commit('clearStore');
    router.push({ path: '/auth' });
  } else if (state === 'signedIn') {
    user = await getUser();
    let path = decodeURIComponent(
      router.history.current.fullPath
        .replace('/auth?redirect=', '')
        .replace('/auth', '/')
    );
    router.push({ path: path });
  }
});

async function getUser(isFlowAndDatabaseItemRequired = true) {
  return Vue.prototype.$Amplify.Auth.currentAuthenticatedUser()
    .then(async (data) => {
      if (data && data.signInUserSession) {
        store.commit('setUser', data);
        setAdminStatus(data);

        if (!mounted) {
          // Set loading
          store.commit('setLoading', true);

          // Get all resources
          if (isFlowAndDatabaseItemRequired) {
            store.dispatch('runPostLoginSync');
            store.dispatch('getFlows');
            store.dispatch('fetchFlowAccessLevel');
            store.dispatch('getUserSettings');
            store.dispatch('fetchWorkFlowTags');
            store.dispatch('fetchFormBuilderIcons');
            store.dispatch('getUserSettingsDB');
          } else {
            store.dispatch('getUserSettings');
          }
          mounted = true;

          // Remove loading
          store.commit('setLoading', false);
        }
        return data;
      }
      return null;
    })
    .catch(() => {
      store.commit('setUser', null);
      return null;
    });
}

async function postEncryptedMessageToParent() {
  try {
    if (window?.parent) {
      const session = await AmplifyModules.Auth.currentSession();

      // Extract tokens
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();

      const data = {
        idToken,
        accessToken,
        refreshToken,
      };
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(data),
        crypto_secret
      ).toString();
      window.parent.postMessage({ type: 'Auth', payload: encryptedData }, '*');
    }
  } catch (error) {
    console.log('error while posting message to parent:>> ', error);
  }
}

function decryptAuthMessage(encryptedData) {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, crypto_secret);
    const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    return decryptedData;
  } catch (error) {
    return null;
  }
}

// Function to handle login via posted message
function setupMessageListener() {
  window.addEventListener('message', async (event) => {
    const data = event.data;
    if (data.type == 'AuthDetails' && data?.payload) {
      try {
        const decryptedData = decryptAuthMessage(data.payload);
        if (
          !decryptedData &&
          !decryptedData.idToken &&
          !decryptedData.accessToken &&
          !decryptedData.refreshToken
        )
          return;
        // Extract tokens from the message
        const { idToken, accessToken, refreshToken } = decryptedData;

        // Create token objects
        const idTokenObj = new CognitoIdToken({ IdToken: idToken });
        const accessTokenObj = new CognitoAccessToken({
          AccessToken: accessToken,
        });
        const refreshTokenObj = new CognitoRefreshToken({
          RefreshToken: refreshToken,
        });

        // Manually set the session
        const session = new CognitoUserSession({
          IdToken: idTokenObj,
          AccessToken: accessTokenObj,
          RefreshToken: refreshTokenObj,
        });

        // Attach session to the Cognito user
        const cognitoUser = new CognitoUser({
          Username: session.getIdToken().decodePayload().email, // Ensure this matches your application logic
          Pool: userPool,
        });

        cognitoUser.setSignInUserSession(session);
        if (window?.parent) {
          window.parent.postMessage(
            {
              type: 'Authorized',
              payload: { authorized: true },
            },
            '*'
          );
        }
      } catch (error) {
        console.log('Error setting user session:', error);
        if (window?.parent) {
          window.parent.postMessage(
            {
              type: 'Authorized',
              payload: { authorized: false },
            },
            '*'
          );
        }
      }
    }
  });
}

// Call the function to set up the listener
setupMessageListener();

// Catch errors from router
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};

const routes = [
  {
    path: '/load-profile-details',
    name: 'loadProfileDetails',
    component: LoadProfileDetails,
    meta: { requiresAuth: true },
  },

  {
    path: '/process-runner/:id',
    name: 'process-runner',
    component: ProcessRunner,
    props: true,
    meta: { requiresAuth: false },
  },
  {
    path: '/auth',
    name: 'auth',
    component: AuthPage,
    props: true,
    beforeEnter: isUserNotLoggedIn,
  },
  {
    path: '/impressum',
    name: 'impressum',
    component: Impressum,
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: Privacy,
  },
  {
    path: '/',
    name: 'home',
    component: Home,
    props: (route) => ({
      section: route.query.section,
      database: route.query.database,
      processID: route.query.process,
      itemID: route.query.item,
    }),
    meta: { requiresAuth: true },
  },
  {
    path: '/redirect/:id',
    name: 'redirect',
    component: redirect,
    props: true,
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: AccessDenied,
  },
  {
    path: '/page-not-found',
    name: 'pageNotFound',
    component: PageNotFound,
  },
  {
    path: '/meeting-subscription',
    name: 'meeting-subscription',
    component: () => import('./pages/MeetingSubscriptionPage.vue'),
  },
  {
    path: '/form-builder/:version/:processID',
    name: 'form-builder',
    component: () => import('./pages/Process.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '(.*)',
    component: PageNotFound,
  },
];

//components.Authenticator
export const router = new Router({
  routes,
});

setupAuthStateSyncListeners();

router.beforeResolve(async (to, from, next) => {
  if (window?.parent) {
    if (from?.name == 'auth') postEncryptedMessageToParent();
    window.parent.postMessage(
      {
        type: 'ROUTE_CHANGED',
        payload: { fullPath: to?.fullPath, query: to?.query },
      },
      '*'
    );
  }
  if (isRouteProtected(to)) {
    const flowList = store.getters.getAllFlows;
    if (from.name === 'process-runner' && !flowList.length) {
      mounted = false;
    }
    user = await getUser();
    if (!user) {
      return next({
        path: '/auth',
        query: {
          redirect: to.fullPath,
        },
      });
    }
    // }

    return next();
  } else if (
    isRoutePublic(to) &&
    ['process-runner', 'form-builder'].includes(to.name)
  ) {
    user = await getUser(false);
  } else if (to.name == 'auth') {
    mounted = false;
  }
  return next();
});

new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount('#app');
